import React, { useEffect, useRef, useState } from "react";
import BoardBackground from "./BoardBackground";
import BoardPath from "./BoardPath";
import soundManager from "audio/OrtivisSounds";

const OrtivisBoardGame = ({ room, onMoveComplete }) => {
	const [boardCases, setBoardCases] = useState([]);
	const [players, setPlayers] = useState([]);
	const [isMoving, setIsMoving] = useState(false);

	const boardRef = useRef(null);
	const playerRefs = useRef([]);
	const prevPlayersRef = useRef([]);

	const [screenHeight, setScreenHeight] = useState(window.innerHeight);

	useEffect(() => {
		const handleResize = () => {
			setScreenHeight(window.innerHeight);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const movePlayer = (playerIndex, newPosition) => {
		return new Promise((resolve) => {
			setIsMoving(true);

			const player = players[playerIndex];
			if (!player) return resolve();

			const startPosition = player.position;
			const steps = Math.abs(newPosition - startPosition);
			const stepDuration = 500; // Time for each step

			let step = 0;
			const stepDirection = Math.sign(newPosition - startPosition);

			const moveInterval = setInterval(() => {
				setPlayers((prevPlayers) =>
					prevPlayers.map((p, i) =>
						i === playerIndex
							? {
									...p,
									position: startPosition + stepDirection * step,
									jumping: true, // Enable jumping animation
							  }
							: p
					)
				);
				step++;

				if (step > steps) {
					clearInterval(moveInterval);
					setTimeout(() => {
						setPlayers((prevPlayers) =>
							prevPlayers.map((p, i) =>
								i === playerIndex ? { ...p, jumping: false } : p
							)
						);

						setIsMoving(false);
						resolve();
						onMoveComplete?.(startPosition + stepDirection * steps);
					}, 1000);
				} else {
					// soundManager.playSound("pawnMove");
				}
			}, stepDuration);
		});
	};

	/** Detect player position changes and trigger smooth movement */
	useEffect(() => {
		if (!room) return;
		setBoardCases(room.board_cases || []);

		const updatedPlayers = room.players || [];
		if (prevPlayersRef.current.length === 0) {
			setPlayers(updatedPlayers);
		} else {
			updatedPlayers.forEach((player, index) => {
				const prevPlayer = prevPlayersRef.current[index];

				if (prevPlayer && prevPlayer.position !== player.position) {
					movePlayer(index, player.position);
				}
			});
		}

		prevPlayersRef.current = updatedPlayers;
	}, [room]);

	useEffect(() => {
		const currentIndex = room.current_player_index ?? 0;

		if (!playerRefs.current[currentIndex]) {
			console.warn("⚠️ PlayerRef is undefined for index:", currentIndex);
			return;
		}

		if (!boardRef.current) {
			console.warn("⚠️ boardRef is undefined!");
			return;
		}

		requestAnimationFrame(() => {
			const playerElement = playerRefs.current[currentIndex];
			const boardElement = boardRef.current;

			if (!playerElement || !boardElement) return;

			// Get the position **relative to the viewport**
			const playerRect = playerElement.getBoundingClientRect();
			const boardRect = boardElement.getBoundingClientRect();

			// Compute the **desired scroll position**
			const scrollTarget =
				boardElement.scrollLeft +
				(playerRect.left - boardRect.left) -
				boardElement.clientWidth / 2 +
				playerElement.clientWidth / 2;

			// Smoothly scroll only if necessary
			if (Math.abs(boardElement.scrollLeft - scrollTarget) > 10) {
				boardElement.scrollTo({
					left: scrollTarget,
					behavior: "smooth",
				});
			}
		});
	}, [players, room.current_player_index]);

	return (
		<div
			ref={boardRef}
			className={`relative w-screen h-screen overflow-x-auto flex ${
				isMoving ? "overflow-hidden" : "scroll-smooth"
			}`}
		>
			{/* Background Worlds */}
			<BoardBackground
				boardCasesLength={boardCases.length}
				screenHeight={screenHeight}
			/>

			{/* Board Path & Players */}
			<BoardPath
				boardCases={boardCases}
				players={players}
				currentPlayerIndex={room.current_player_index}
				playerRefs={playerRefs}
				screenHeight={screenHeight}
			/>
		</div>
	);
};

export default OrtivisBoardGame;
